import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Shield,
  Info,
  FileText,
  ExternalLink,
  Mail,
  BookOpen,
  Scale,
  AlertCircle,
} from "lucide-react";
import MainLayout from "@/components/layouts/MainLayout";
import { motion } from "framer-motion";

const TermsOfService = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service | Numberz Insight</title>
        <meta
          name="description"
          content="Terms of Service for Numberz Insight - Understand the terms and conditions that govern your use of our services."
        />
      </Helmet>

      <MainLayout>
        <div className="relative bg-gradient-to-b from-primary/10 to-background pt-24 pb-16 md:pt-28 md:pb-20 overflow-hidden">
          <div className="absolute inset-0 z-0 opacity-30">
            <img
              src="https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80"
              alt="Terms background"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-background"></div>
          </div>
          <div className="container relative z-10 mx-auto px-4 max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Terms of Service
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Please read these terms carefully as they govern your use of our
                services
              </p>
              <div className="flex items-center justify-center mt-6 text-sm text-muted-foreground">
                <BookOpen className="h-4 w-4 mr-2" />
                <span>
                  Last updated:{" "}
                  {new Date().toLocaleDateString("en-AU", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        <main className="container mx-auto px-4 py-8 max-w-5xl">
          <Tabs defaultValue="general" className="w-full">
            <div className="sticky top-20 z-10 bg-background/80 backdrop-blur-sm pt-4 pb-2 mb-8">
              <TabsList className="w-full justify-start overflow-x-auto flex p-1 rounded-lg bg-muted/50">
                <TabsTrigger
                  value="general"
                  className="rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                  <Info className="h-4 w-4 mr-2" />
                  General Terms
                </TabsTrigger>
                <TabsTrigger
                  value="services"
                  className="rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Services & Fees
                </TabsTrigger>
                <TabsTrigger
                  value="legal"
                  className="rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"
                >
                  <Scale className="h-4 w-4 mr-2" />
                  Legal Provisions
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="general" className="space-y-6">
              <Alert className="bg-primary/5 border-primary/20">
                <Info className="h-4 w-4 text-primary" />
                <AlertTitle>About Our Terms</AlertTitle>
                <AlertDescription>
                  These terms establish the legal agreement between you and
                  Numberz Insight.
                </AlertDescription>
              </Alert>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <BookOpen className="h-5 w-5 mr-2 text-primary" />
                    Introduction
                  </CardTitle>
                  <CardDescription>
                    The legal framework for using our services
                  </CardDescription>
                </CardHeader>
                <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                  <p>
                    These Terms of Service ("Terms") govern your use of the
                    website, products, and services ("Services") provided by
                    Numberz Insight Pty Ltd (ABN: 55 644 962 167) ("we", "our",
                    "us").
                  </p>
                  <p>
                    By accessing or using our Services, you agree to be bound by
                    these Terms. If you do not agree to these Terms, please do
                    not use our Services.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <FileText className="h-5 w-5 mr-2 text-primary" />
                    Definitions
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                  <p>In these Terms:</p>
                  <ul>
                    <li>
                      "Client", "you", or "your" refers to any individual or
                      entity that uses our Services.
                    </li>
                    <li>
                      "Content" means any information, data, text, software,
                      graphics, or other materials displayed on or accessible
                      through our Services.
                    </li>
                    <li>
                      "Intellectual Property" includes all patents, trademarks,
                      copyright, designs, trade secrets, know-how, and other
                      intellectual property rights.
                    </li>
                    <li>
                      "Website" refers to the website operated by us at
                      www.numberz.au and any associated subdomains.
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <AlertCircle className="h-5 w-5 mr-2 text-primary" />
                    Client Obligations
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                  <p>As a client, you agree to:</p>
                  <ul>
                    <li>
                      Provide accurate, complete, and up-to-date information as
                      required for the provision of our Services
                    </li>
                    <li>
                      Respond promptly to requests for additional information or
                      clarification
                    </li>
                    <li>
                      Review all documents prepared by us before they are lodged
                      or finalized
                    </li>
                    <li>
                      Pay all fees and charges associated with our Services in a
                      timely manner
                    </li>
                    <li>Comply with all applicable laws and regulations</li>
                    <li>Use our Services only for lawful purposes</li>
                    <li>
                      Maintain the confidentiality of any account credentials
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="services" className="space-y-6">
              <Alert className="bg-primary/5 border-primary/20">
                <FileText className="h-4 w-4 text-primary" />
                <AlertTitle>Our Services & Fees</AlertTitle>
                <AlertDescription>
                  Information about the services we provide and associated fees.
                </AlertDescription>
              </Alert>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <FileText className="h-5 w-5 mr-2 text-primary" />
                    Services
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                  <p>
                    We provide tax, accounting, and business registration
                    services to individuals and businesses in Australia. Our
                    specific services include:
                  </p>
                  <ul>
                    <li>Tax return preparation and lodgment</li>
                    <li>
                      Business registration services (ABN, GST, ACN, company
                      registration)
                    </li>
                    <li>Accounting and bookkeeping services</li>
                    <li>Financial planning and advisory services</li>
                    <li>Other related professional services</li>
                  </ul>
                  <p>
                    We reserve the right to modify, suspend, or discontinue any
                    part of our Services at any time without notice.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <FileText className="h-5 w-5 mr-2 text-primary" />
                    Fees and Payment
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                  <p>
                    Our fees for Services will be as agreed with you before the
                    commencement of any work. We may require payment of a
                    deposit before commencing work.
                  </p>
                  <p>
                    Payment terms are strictly 14 days from the date of invoice
                    unless otherwise agreed in writing. We accept payment via
                    electronic funds transfer, credit card, and other methods as
                    specified on our invoices.
                  </p>
                  <p>
                    We reserve the right to charge interest on overdue amounts
                    at the rate of 10% per annum, calculated daily from the due
                    date until payment is received in full.
                  </p>
                  <p>
                    All fees are quoted in Australian Dollars (AUD) and include
                    GST unless otherwise stated.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <Shield className="h-5 w-5 mr-2 text-primary" />
                    Confidentiality
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                  <p>
                    We will maintain the confidentiality of all information
                    provided by you in connection with our Services, except as
                    required by law or as authorized by you.
                  </p>
                  <p>
                    We may disclose your information to third parties as
                    necessary to provide our Services, such as to the Australian
                    Taxation Office or other government agencies.
                  </p>
                  <p>
                    Our full Privacy Policy, which explains how we collect, use,
                    and protect your personal information, is available at{" "}
                    <Link
                      to="/privacy"
                      className="text-primary hover:underline"
                    >
                      www.numberz.au/privacy
                    </Link>
                    .
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="legal" className="space-y-6">
              <Alert className="bg-primary/5 border-primary/20">
                <Scale className="h-4 w-4 text-primary" />
                <AlertTitle>Legal Provisions</AlertTitle>
                <AlertDescription>
                  Important legal terms regarding liability, intellectual
                  property, and dispute resolution.
                </AlertDescription>
              </Alert>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <Shield className="h-5 w-5 mr-2 text-primary" />
                    Intellectual Property
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                  <p>
                    All Intellectual Property in our Services, Website, and
                    Content is owned by us or our licensors. Nothing in these
                    Terms transfers any Intellectual Property rights to you.
                  </p>
                  <p>
                    You may not reproduce, modify, adapt, distribute, publish,
                    or create derivative works from any part of our Services,
                    Website, or Content without our prior written consent.
                  </p>
                  <p>
                    You grant us a non-exclusive, worldwide, royalty-free
                    license to use, reproduce, and modify any materials or
                    information you provide to us for the purpose of providing
                    our Services to you.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <Scale className="h-5 w-5 mr-2 text-primary" />
                    Limitation of Liability
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                  <p>
                    To the maximum extent permitted by law, we exclude all
                    liability for any loss or damage of any kind (including
                    direct, indirect, special, or consequential loss) arising
                    from or in connection with our Services or these Terms.
                  </p>
                  <p>
                    Where liability cannot be excluded, our liability to you for
                    any loss or damage arising from or in connection with our
                    Services or these Terms is limited to the lesser of:
                  </p>
                  <ul>
                    <li>
                      The amount paid by you to us for the relevant Services; or
                    </li>
                    <li>AUD $1,000</li>
                  </ul>
                  <p>
                    This limitation of liability does not apply to any liability
                    that cannot be excluded by law, including liability for
                    death or personal injury caused by negligence, or liability
                    for fraud or fraudulent misrepresentation.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <Scale className="h-5 w-5 mr-2 text-primary" />
                    Indemnity & Termination
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                  <h3>Indemnity</h3>
                  <p>
                    You agree to indemnify and hold harmless Numberz Insight Pty
                    Ltd, its officers, directors, employees, and agents from and
                    against any claims, liabilities, damages, losses, and
                    expenses, including without limitation reasonable legal and
                    accounting fees, arising out of or in any way connected with
                    your access to or use of our Services or your violation of
                    these Terms.
                  </p>

                  <h3>Termination</h3>
                  <p>
                    We may terminate or suspend your access to our Services
                    immediately, without prior notice or liability, for any
                    reason, including without limitation if you breach these
                    Terms.
                  </p>
                  <p>
                    You may terminate your engagement with us at any time by
                    providing written notice. You will remain liable for payment
                    of all fees for Services provided up to the date of
                    termination.
                  </p>
                  <p>
                    Upon termination, your right to use our Services will
                    immediately cease.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <Scale className="h-5 w-5 mr-2 text-primary" />
                    Governing Law & Changes
                  </CardTitle>
                </CardHeader>
                <CardContent className="prose dark:prose-invert max-w-none text-muted-foreground">
                  <h3>Governing Law</h3>
                  <p>
                    These Terms are governed by the laws of Victoria, Australia.
                    You and we irrevocably submit to the exclusive jurisdiction
                    of the courts of Victoria, Australia.
                  </p>

                  <h3>Changes to Terms</h3>
                  <p>
                    We reserve the right to modify these Terms at any time. We
                    will provide notice of any material changes by posting the
                    updated Terms on our Website or by other means as required
                    by law.
                  </p>
                  <p>
                    Your continued use of our Services after any changes to
                    these Terms constitutes your acceptance of the new Terms.
                  </p>

                  <h3>Severability</h3>
                  <p>
                    If any provision of these Terms is found to be invalid or
                    unenforceable, that provision shall be enforced to the
                    maximum extent possible, and the other provisions of these
                    Terms shall remain in full force and effect.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="mt-16 pt-8 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                  <BookOpen className="h-5 w-5 mr-2 text-primary" />
                  Terms Commitment
                </h3>
                <p className="text-muted-foreground">
                  We're committed to providing clear and fair terms for all our
                  clients and ensuring transparency in our business practices.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-primary" />
                  Related Documents
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link
                      to="/privacy"
                      className="text-primary hover:underline flex items-center"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/email/terms"
                      className="text-primary hover:underline flex items-center"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Email Terms of Service
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-4 flex items-center">
                  <Mail className="h-5 w-5 mr-2 text-primary" />
                  Contact Us
                </h3>
                <p className="text-muted-foreground mb-4">
                  Have questions about our terms?
                </p>
                <Link to="/contact">
                  <Button variant="default">Contact Us</Button>
                </Link>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-border text-center">
              <p className="text-sm text-muted-foreground">
                Last updated:{" "}
                {new Date().toLocaleDateString("en-AU", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </p>
            </div>
          </div>
        </main>
      </MainLayout>
    </>
  );
};

export default TermsOfService;
